import { useCallback, useEffect, useMemo, useState } from "react";
import { XtLocale, Util } from "@xt/xtbase";
import {
  fetchAcademyConfigListParsed,
  resolveAcademyConfigItemLink,
  resolveAcademyConfigAssetUrl,
} from "@/api/academy-config";
import { getNewsList } from "@/api/xt-app";
import { PUBLIC_FEED_TAB } from "./publicFeedConstants";

const { moment } = Util;

function formatAcademyConfigCreatedTime(ts) {
  const n = Number(ts);
  if (!Number.isFinite(n) || n <= 0) return "";
  const ms = n < 1e12 ? n * 1000 : n;
  const m = moment(ms);
  if (!m.isValid()) return "";
  return m.format("YYYY-MM-DD HH:mm");
}

function formatDayLabel(dayKey, langCode) {
  const m = moment(dayKey, "YYYY-MM-DD");
  if (!m.isValid()) return dayKey;
  if (String(langCode || "")
    .toLowerCase()
    .startsWith("zh")) {
    return m.format("M月D日");
  }
  return m.format("ll");
}

function mapAnnouncementRows(rows) {
  return (rows || [])
    .slice()
    .sort((a, b) => Number(b.weights || 0) - Number(a.weights || 0))
    .map((row) => ({
      id: row.id,
      title: row.title || "",
      date: formatAcademyConfigCreatedTime(row.createdTime),
      url: resolveAcademyConfigItemLink(row),
    }));
}

function mapAcademyRows(rows) {
  return (rows || [])
    .slice()
    .sort((a, b) => Number(b.weights || 0) - Number(a.weights || 0))
    .map((row) => {
      const tags = Array.isArray(row.tags)
        ? row.tags.map((x) => (x == null ? "" : String(x).trim())).filter(Boolean)
        : [];
      const coverRaw =
        row.coverImage != null && String(row.coverImage).trim()
          ? String(row.coverImage).trim()
          : "";
      return {
        id: row.id,
        title: row.title || "",
        url: resolveAcademyConfigItemLink(row),
        tags,
        coverImageSrc: resolveAcademyConfigAssetUrl(coverRaw),
      };
    });
}

export default function usePublicFeedContent() {
  const [announcementList, setAnnouncementList] = useState([]);
  const [newsList, setNewsList] = useState([]);
  const [academyList, setAcademyList] = useState([]);
  const [announcementLoading, setAnnouncementLoading] = useState(false);
  const [newsLoading, setNewsLoading] = useState(false);
  const [academyLoading, setAcademyLoading] = useState(false);

  const loadAnnouncementAndAcademy = useCallback(async () => {
    try {
      setAnnouncementLoading(true);
      setAcademyLoading(true);
      const [annRows, acaRows] = await Promise.all([
        fetchAcademyConfigListParsed({ configType: 2, platform: "web" }),
        fetchAcademyConfigListParsed({ configType: 1, platform: "web" }),
      ]);
      setAnnouncementList(mapAnnouncementRows(annRows));
      setAcademyList(mapAcademyRows(acaRows));
    } catch {
      setAnnouncementList([]);
      setAcademyList([]);
    } finally {
      setAnnouncementLoading(false);
      setAcademyLoading(false);
    }
  }, []);

  const loadNews = useCallback(async () => {
    try {
      setNewsLoading(true);
      const res = await getNewsList({
        params: { pageIndex: 1, pageSize: 999 },
      });
      const raw = Array.isArray(res) ? res : (res && res.data) || [];
      const list = raw.map((n) => {
        const ts = n.publishedAt || n.createdAt || Date.now();
        return {
          id: n.id,
          title: n.title,
          url: n.url,
          publishedAt: ts,
          timeStr: moment(ts).format("HH:mm"),
          dayKey: moment(ts).format("YYYY-MM-DD"),
        };
      });
      setNewsList(list);
    } catch {
      setNewsList([]);
    } finally {
      setNewsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAnnouncementAndAcademy();
    loadNews();
  }, [loadAnnouncementAndAcademy, loadNews]);

  const langCode = (XtLocale.intl && XtLocale.intl.locale) || "en";

  const newsGrouped = useMemo(() => {
    if (!newsList.length) return [];
    const byDay = new Map();
    for (const item of newsList) {
      const { dayKey } = item;
      if (!byDay.has(dayKey)) byDay.set(dayKey, []);
      byDay.get(dayKey).push(item);
    }
    const keys = [...byDay.keys()].sort((a, b) => b.localeCompare(a));
    return keys.map((dayKey) => ({
      dayKey,
      label: formatDayLabel(dayKey, langCode),
      items: byDay.get(dayKey).sort((a, b) => b.publishedAt - a.publishedAt),
    }));
  }, [newsList, langCode]);

  const getMoreUrl = useCallback((tabKey) => {
    const zenLang = XtLocale.getZenLan();
    if (tabKey === PUBLIC_FEED_TAB.ANNOUNCEMENT) {
      return `https://spikex.zendesk.com/hc/${zenLang}/categories/5284067920670-Announcements`;
    }
    if (tabKey === PUBLIC_FEED_TAB.ACADEMY) {
      return `https://spikex.zendesk.com/hc/${zenLang}/categories/5284051234846-Academy`;
    }
    return "";
  }, []);

  const getFeedState = useCallback(
    (activeTab) => {
      switch (activeTab) {
        case PUBLIC_FEED_TAB.NEWS:
          return { list: newsList, loading: newsLoading, grouped: newsGrouped };
        case PUBLIC_FEED_TAB.ACADEMY:
          return { list: academyList, loading: academyLoading, grouped: null };
        case PUBLIC_FEED_TAB.ANNOUNCEMENT:
        default:
          return { list: announcementList, loading: announcementLoading, grouped: null };
      }
    },
    [
      announcementList,
      announcementLoading,
      newsList,
      newsLoading,
      newsGrouped,
      academyList,
      academyLoading,
    ]
  );

  return { getFeedState, getMoreUrl };
}
